import { useV2ListAccountHealthSnapshotsQuery } from "@generated";
import { useMemo } from "react";
import { AccountHealthSnapshot, AccountHealthStatus } from "../types";

// Two-step fetch:
//   1. Probe a tiny page (size 1) — the API's default sort is week_start desc,
//      so the first record's week-start is the most recent week with any
//      snapshot. We use a probe rather than just trusting "page 1 of size N"
//      because the snapshot cron may have failed for some accounts that week.
//   2. Refetch filtered to that exact week-start, with `include: ["account"]`
//      so each row's account name + slug arrives in the same response.
const useLatestWeekSnapshots = () => {
  const {
    data: probe,
    isLoading: probeLoading,
    error: probeError,
  } = useV2ListAccountHealthSnapshotsQuery({ page: { size: 1, number: 1 } });

  const latestWeekStart = useMemo<string | null>(
    () => probe?.data?.[0]?.attributes?.["week-start"] ?? null,
    [probe]
  );

  const {
    data,
    isLoading: snapsLoading,
    isFetching: snapsFetching,
    error: snapsError,
    refetch,
  } = useV2ListAccountHealthSnapshotsQuery(
    {
      filter: { "week-start": latestWeekStart ?? "" },
      include: ["account"],
      page: { size: 250, number: 1 },
    },
    { skip: !latestWeekStart }
  );

  const accountsById = useMemo(() => {
    const map = new Map<string, { name: string; slug: string }>();
    for (const a of data?.included ?? []) {
      if (a.id) {
        map.set(String(a.id), {
          name: a.attributes?.name ?? "",
          slug: a.attributes?.slug ?? "",
        });
      }
    }
    return map;
  }, [data]);

  const snapshots: AccountHealthSnapshot[] = useMemo(() => {
    if (!data?.data) return [];
    return data.data
      .filter((s) => !!s.id)
      .map((s) => {
        const accountRel = (
          s.relationships as Record<string, { data?: unknown }> | undefined
        )?.account?.data as { id?: number | string } | undefined;
        const accountId = accountRel?.id ? String(accountRel.id) : "";
        const account = accountsById.get(accountId) ?? { name: "", slug: "" };
        const attrs = s.attributes ?? {};
        return {
          id: String(s.id),
          weekStart: attrs["week-start"] ?? "",
          status: (attrs.status ?? "healthy") as AccountHealthStatus,
          primaryDriver: attrs["primary-driver"] ?? "",
          signinSlope: Number(attrs["signin-slope"] ?? 0),
          projectSlope: Number(attrs["project-slope"] ?? 0),
          projectBaselineRatio: Number(attrs["project-baseline-ratio"] ?? 0),
          accountId,
          accountName: account.name,
          accountSlug: account.slug,
        };
      });
  }, [data, accountsById]);

  return {
    snapshots,
    latestWeekStart,
    isLoading: probeLoading || (latestWeekStart && snapsLoading),
    isFetching: snapsFetching,
    error: probeError ?? snapsError,
    refetch,
  };
};

export default useLatestWeekSnapshots;
